import * as React from 'react';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1beta1/email_address';
import type {
  GetUserEmailRequest,
  GetUserEmailResponse,
} from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1beta1/user_emails_api';

import Button from 'bundles/internal-ui/components/buttons/Button';
import Card from 'bundles/internal-ui/components/cards/Card';
import TextBox from 'bundles/internal-ui/components/forms/TextBox';

import 'css!./__styles__/EnterpriseToolsHome';

type PropsFromCaller = {
  params: { learnerEmailAddr?: string };
};

type PropsFromGraphql = {
  userEmail?: UserEmail;
};

type UserEmailQueryVariables = {
  input: GetUserEmailRequest;
};

type UserEmailQueryResult = {
  GetUserEmail: GetUserEmailResponse;
};

type Props = PropsFromCaller & PropsFromGraphql;

type State = {
  errorMessage?: string;
  query: string;
};

class EnterpriseToolsHome extends React.Component<Props, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof EnterpriseToolsHome.contextTypes>;

  state = {
    errorMessage: undefined,
    query: '',
  };

  componentDidMount() {
    this.checkLearnerEmail(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps: $TSFixMe) {
    this.checkLearnerEmail(nextProps);
  }

  handleUpdateText = (e: React.ChangeEvent<any>) => {
    this.setState({ query: e.target.value });
  };

  onInputKeyPress = (e: React.KeyboardEvent<any>) => {
    if (e.key === 'Enter') this.onViewEnterpriseDetails();
  };

  onViewEnterpriseDetails = () => {
    const query = this.state.query.trim();
    if (query.indexOf('@') > -1) {
      this.context.router.push(`/internal/enterprise/email/${query}`);
    } else if (!/^\d+$/.test(query)) {
      // Test if all digits
      this.setState({ errorMessage: `Invalid UID: ${query}. Must contain only digits.` });
    } else {
      this.redirectToEnterpriseInfo(query);
    }
  };

  checkLearnerEmail = (props: $TSFixMe) => {
    if (props.params.learnerEmailAddr && !props.userEmail) {
      this.setState({ errorMessage: `Email ${props.params.learnerEmailAddr} not found!` });
    }
    if (props.userEmail) this.redirectToEnterpriseInfo(props.userEmail.userId);
  };

  redirectToEnterpriseInfo = (userId: string) => {
    this.context.router.push(`/internal/enterprise/user/${userId}/memberships`);
  };

  render() {
    return (
      <div className="rc-EnterpriseToolsHome">
        <Card>
          <h3>View enterprise information for user:</h3>
          <TextBox
            placeholder="Email or User ID"
            onUpdateField={this.handleUpdateText}
            currentValue={this.state.query}
            inputProps={{ onKeyPress: this.onInputKeyPress, autoFocus: true }}
          />
          <br />
          <Button text="See Programs" btnStyle="info" onClick={this.onViewEnterpriseDetails} />
          {this.state.errorMessage && (
            <div>
              <br />
              <pre style={{ whiteSpace: 'pre-wrap' }}>{this.state.errorMessage}</pre>
            </div>
          )}
        </Card>
      </div>
    );
  }
}

const UserEmailQuery = gql`
  query UserEmailQuery($input: GetUserEmailRequest!) {
    GetUserEmail(input: $input)
      @rest(type: "GetUserEmail", path: "grpc/useremails/v1beta1/UserEmailsAPI/GetUserEmail", method: "POST") {
      userEmail
    }
  }
`;

const withUserEmail = graphql<PropsFromCaller, UserEmailQueryResult, UserEmailQueryVariables, PropsFromGraphql>(
  UserEmailQuery,
  {
    skip: ({ params }) => !params.learnerEmailAddr,
    options: ({ params }) => ({ variables: { input: { emailAddress: params.learnerEmailAddr } } }),
    props: ({ data }) => {
      return { userEmail: data?.GetUserEmail?.userEmail };
    },
  }
);

export default withUserEmail(EnterpriseToolsHome);
